import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cpu, CheckCircle, Loader2, FileText, PieChart, ArrowRight } from 'lucide-react';
import { useApp } from '../context/AppContext';

const Builder = () => {
    const [step, setStep] = useState('input');
    const [idea, setIdea] = useState('');
    const [loadingText, setLoadingText] = useState('Initializing...');
    const { createBusiness, setActiveBusinessId } = useApp();
    const navigate = useNavigate();

    // Fake AI Result Data
    const generatedData = {
        name: "Farm2Table AI",
        tagline: "Autonomous logistics for local agriculture",
        description: "A platform connecting local farmers directly to restaurants using AI routing.",
        tam: "$42B",
        pricing: "Freemium + 5% Commission",
        competitors: ["Sysco", "BlueCart", "LocalLine"]
    };

    const handleGenerate = () => {
        if (!idea) return;
        setStep('processing');

        const stages = [
            "Scanning Global Markets...",
            "Analyzing Competitor Data...",
            "Calculating Financial Models...",
            "Designing Brand Identity...",
            "Finalizing Business Structure..."
        ];

        stages.forEach((text, index) => {
            setTimeout(() => {
                setLoadingText(text);
                if (index === stages.length - 1) setStep('results');
            }, (index + 1) * 1200);
        });
    };

    const handleSave = () => {
        const newId = createBusiness({
            name: generatedData.name,
            tagline: generatedData.tagline,
            demand: 92
        });
        setActiveBusinessId(newId);
        navigate('/app/workspace');
    };

    return (
        <div className="max-w-4xl mx-auto py-8">
            {/* Step 1: Input */}
            {step === 'input' && (
                <div className="animate-slide-up text-center">
                    <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center text-primary mx-auto mb-6">
                        <Cpu size={32} />
                    </div>
                    <h1 className="text-4xl font-bold text-slate-900 mb-4">What do you want to build?</h1>
                    <p className="text-slate-500 mb-8">Describe your idea in plain English. Our AI will handle the MBA-level work.</p>

                    <div className="card p-2 max-w-2xl mx-auto shadow-lg">
                        <textarea
                            className="w-full p-4 outline-none text-lg min-h-[150px] resize-none rounded-lg"
                            placeholder="e.g. An app that helps dog owners find safe walking routes..."
                            value={idea}
                            onChange={e => setIdea(e.target.value)}
                        />
                        <div className="flex justify-between items-center px-4 pb-2 pt-2 border-t border-border">
                            {/* NAME UPDATE */}
                            <span className="text-xs font-bold text-slate-400 uppercase">Model: AutoInventor-GPT 4.0</span>
                            <button onClick={handleGenerate} disabled={!idea} className="btn btn-primary px-6">
                                Generate Business <ArrowRight size={16} className="ml-2" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Step 2: Processing */}
            {step === 'processing' && (
                <div className="text-center py-20 animate-fade-in">
                    <div className="relative w-24 h-24 mx-auto mb-8">
                        <div className="absolute inset-0 border-4 border-indigo-100 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">{loadingText}</h2>
                    <p className="text-slate-500 mt-2">This usually takes a few seconds...</p>
                </div>
            )}

            {/* Step 3: Results */}
            {step === 'results' && (
                <div className="animate-slide-up space-y-6">
                    <div className="flex justify-between items-center">
                        <h1 className="text-3xl font-bold text-slate-900">Blueprint Ready</h1>
                        <button onClick={handleSave} className="btn btn-primary">Launch Workspace</button>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Brand Card */}
                        <div className="card p-6 md:col-span-2">
                            <div className="flex items-start gap-4">
                                <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white font-bold text-2xl">F2T</div>
                                <div>
                                    <h3 className="text-xl font-bold">{generatedData.name}</h3>
                                    <p className="text-slate-500">{generatedData.tagline}</p>
                                    <p className="mt-4 text-slate-700 leading-relaxed">{generatedData.description}</p>
                                </div>
                            </div>
                        </div>

                        {/* Score Card */}
                        <div className="card p-6 flex flex-col items-center justify-center text-center bg-slate-900 text-white">
                            <div className="text-5xl font-bold text-green-400 mb-2">92</div>
                            <div className="text-sm font-medium opacity-80">Market Demand Score</div>
                        </div>

                        {/* Details Grid */}
                        <div className="card p-6">
                            <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><PieChart size={16} /> Market Size (TAM)</h4>
                            <p className="text-2xl font-bold text-primary">{generatedData.tam}</p>
                        </div>

                        <div className="card p-6">
                            <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><FileText size={16} /> Pricing Model</h4>
                            <p className="text-lg font-medium">{generatedData.pricing}</p>
                        </div>

                        <div className="card p-6">
                            <h4 className="font-bold text-slate-900 mb-3">Key Competitors</h4>
                            <div className="flex flex-wrap gap-2">
                                {generatedData.competitors.map(c => (
                                    <span key={c} className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-sm">{c}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
export default Builder;
